// Черновата на НОВА инвентаризация (`components/drafts/` — localStorage +
// сървърът, тип „stocktake"). Форматът (v1) и ключът в localStorage са СЪЩИТЕ
// като на досегашната локална чернова — започнатото преди сървърните чернови
// броене се предлага както досега и при първата промяна отива и на сървъра.
// Приготвените рецепти пътуват в `recipes` (снимката на `useStocktakeRecipes`)
// — незадължително поле: старите чернови го нямат и се зареждат без рецепти.
// ИМЕНАТА на полетата не се пипат: същият обект е и `payload` на сървърната
// чернова, която колега с по-стар FE също чете.
import type { TFunction } from "i18next";
import type { Product } from "../../../../api/inventory";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import type { ConfirmPreviewItem } from "../../components/confirm/confirmTypes";
import {
  isVersionedObject,
  localDraftStore,
  type DraftPayloadBase,
  type DraftSummaryFields,
} from "../../components/drafts/draftModel";
import type { StocktakeRecipeEntry, StocktakeRecipesSnapshot } from "./stocktakeRecipes";
import {
  isRowCounted,
  parseDecimal,
  parseQtyExpression,
  type StocktakeRowDraft,
} from "./stocktakeWorksheetShared";

export type StocktakeDraft = DraftPayloadBase & {
  v: 1;
  date: string; // YYYY-MM-DD
  warehouseId: number | null;
  reason: string;
  rows: Record<number, StocktakeRowDraft>;
  substitutions: { recipientProductId: number; donorProductId: number }[];
  // приготвените рецепти (добавено с тях — старите чернови го нямат)
  recipes?: StocktakeRecipesSnapshot;
};

const parseStocktakeDraft = (raw: unknown): StocktakeDraft | null => {
  if (!isVersionedObject(raw, [1])) return null;
  const draft = raw as unknown as StocktakeDraft;
  if (!draft.rows || typeof draft.rows !== "object") return null;
  return { ...draft, substitutions: draft.substitutions ?? [] };
};

export const STOCKTAKE_DRAFT_STORE = localDraftStore("inventory_stocktake_draft", parseStocktakeDraft);
export const readDraft = STOCKTAKE_DRAFT_STORE.read;
export const clearDraft = STOCKTAKE_DRAFT_STORE.clear;

const countedEntries = (draft: StocktakeDraft) =>
  Object.entries(draft.rows).filter(([, row]) => isRowCounted(row));

// Рецептите на черновата (непознат формат — няма рецепти, както `apply`)
export const draftRecipeEntries = (draft: StocktakeDraft): StocktakeRecipeEntry[] =>
  draft.recipes?.v === 1 && Array.isArray(draft.recipes.entries) ? draft.recipes.entries : [];

// Черновата е за продължаване, ако нещо е преброено — насипно или рецепта
export const hasStocktakeDraftContent = (draft: StocktakeDraft) =>
  countedEntries(draft).length > 0 || draftRecipeEntries(draft).length > 0;

// Копието за списъка: складът е етикетът, редовете са преброените продукти и
// приготвените рецепти („12 преброени реда"); сумата (излишъци − липси) не е
// „обща стойност" и не се показва
export const stocktakeDraftSummary = (
  warehouseName: string | null,
  countedLines: number,
): DraftSummaryFields => ({
  label: warehouseName,
  documentType: null,
  linesCount: countedLines,
  total: null,
});

// Картата на локалната чернова на началния екран
export const localStocktakeDraftSummary = (
  draft: StocktakeDraft,
  warehouseNameOf: (warehouseId: number | null) => string | null,
): DraftSummaryFields | null => {
  const countedLines = countedEntries(draft).length + draftRecipeEntries(draft).length;
  return countedLines ? stocktakeDraftSummary(warehouseNameOf(draft.warehouseId), countedLines) : null;
};

// „Бира с кола — 2 порции" (празните порции — само името)
export const recipePreviewItems = (t: TFunction, entries: StocktakeRecipeEntry[]): ConfirmPreviewItem[] =>
  entries.map((entry) => {
    const portions = parseDecimal(entry.portions);
    return {
      key: `r${entry.recipeId}`,
      label: entry.recipeName,
      value: portions > 0 ? t("inventoryPage.cfmPortions", { count: portions, value: entry.portions }) : undefined,
    };
  });

// Въпросът „Да продължите ли…": рецептите и „Продукт — намерено" (до 5 +
// общият брой), четено както в таблицата: с толкова знака, колкото има
// мерната единица, и мярката
export const stocktakeDraftPreview = (
  t: TFunction,
  draft: StocktakeDraft | null,
  productById: Map<number, Product>,
  decimalsOf: (productId: number) => number,
): { items: ConfirmPreviewItem[]; total: number } => {
  if (!draft) return { items: [], total: 0 };
  const products = countedEntries(draft).map(([productIdText, row]) => {
    const productId = +productIdText;
    const product = productById.get(productId);
    return {
      key: productIdText,
      label: product?.name ?? `#${productIdText}`,
      value: formatQtyUnit(parseQtyExpression(row.counted), decimalsOf(productId), product?.unit),
    };
  });
  const items = [...recipePreviewItems(t, draftRecipeEntries(draft)), ...products];
  return { items: items.slice(0, 5), total: items.length };
};
